/** @format */

import {Box, Typography} from '@mui/material';
import {format} from 'date-fns';
import {Trans} from 'react-i18next';

import {GET_SUSPENDED_RECOMMENDATIONS} from 'pages/Home/Main/Project/components/Recommendation/graphql/recommendation.queries';

import Modal from 'components/Modal';

import Loading from 'components/loading/Loading';

import {
  useCreateRecommendationSuspendedMutation,
  useDeleteRecommendationSuspendedMutation,
  useGetSuspendedRecommendationsQuery,
} from 'pages/Home/Main/Project/components/Recommendation/graphql/__generated__';

import {ActionButton, FooterContainer, FooterText, ModalContent} from './styles';

import {
  AutoTargetingData,
  GenderAgeData,
  NotFoundPagesData,
  RecommendationType,
  RegionData,
  RSAModalProps,
  RSAPlatformData,
} from './interfaces';

import AutoTargetingTable from './components/AutoTargetingTable';
import GenderAgeTable from './components/GenderAgeTable';
import NotFoundPagesTable from './components/NotFoundPagesTable';
import RegionTable from './components/RegionTable';
import RSAPlatformTable from './components/RSAPlatformTable';
import {getDataForDisplay} from './utils';
import {useGetRecommendationItemsQuery} from './graphql/__generated__';
import {RecommendationsItemType} from './types';

export type RSAModalPropsExcluded = Omit<RSAModalProps, 'isOpen'>;

const RSAModal = ({onClose, title, type, isMediaCampaign = false}: RSAModalPropsExcluded) => {
  const {data: recommendationItemsData, loading: isLoadingRecommendationItems} =
    useGetRecommendationItemsQuery({
      variables: {date: format(new Date(), 'yyyy-MM-dd'), sensorName: type},
    });
  const {data: suspendedRecommendations} = useGetSuspendedRecommendationsQuery();

  const [deleteRecommendationFromSuspends] = useDeleteRecommendationSuspendedMutation({
    refetchQueries: [GET_SUSPENDED_RECOMMENDATIONS],
  });

  const [suspendRecommendation] = useCreateRecommendationSuspendedMutation({
    refetchQueries: [GET_SUSPENDED_RECOMMENDATIONS],
  });

  const suspendedRecommendationsResult =
    suspendedRecommendations?.currentUser?.currentCompany?.recommendationSuspendeds;

  const checkIsSuspended = (name: string) => {
    return suspendedRecommendationsResult?.some(
      ({sensorName}: {sensorName: string}) => sensorName === name,
    );
  };

  const handleToggleReadStatus = () => {
    if (checkIsSuspended(type)) {
      const id = suspendedRecommendationsResult?.find(
        ({sensorName}: {sensorName: string}) => sensorName === type,
      )?.id;

      if (!id) return;

      deleteRecommendationFromSuspends({variables: {id}});
    } else {
      suspendRecommendation({variables: {input: {sensorName: type}}});
    }
  };

  const renderTable = () => {
    const recommendationItems: RecommendationsItemType[] =
      recommendationItemsData?.currentUser?.currentCompany?.recommendationItems?.slice() ?? [];

    const displayData = getDataForDisplay(type as RecommendationType, recommendationItems);

    switch (type) {
      case RecommendationType.RSA_PLATFORM:
        return <RSAPlatformTable data={displayData as RSAPlatformData[]} />;
      case RecommendationType.GENDER_AGE:
        return (
          <GenderAgeTable data={displayData as GenderAgeData[]} showCorrection={!isMediaCampaign} />
        );
      case RecommendationType.REGION:
        return <RegionTable data={displayData as RegionData[]} showCorrection={!isMediaCampaign} />;
      case RecommendationType.NOT_FOUND_PAGES:
        return <NotFoundPagesTable data={displayData as NotFoundPagesData[]} />;
      case RecommendationType.AUTO_TARGETING:
        return <AutoTargetingTable data={displayData as AutoTargetingData[]} />;
      default:
        return null;
    }
  };

  if (isLoadingRecommendationItems) return <Loading />;

  return (
    <Modal
      header={
        <Typography variant='h6' sx={{maxWidth: '600px'}}>
          <Trans>{title}</Trans>
        </Typography>
      }
      onClose={onClose}
      showCloseIcon
      variant='light-secondary'
    >
      <ModalContent>
        <Box sx={{maxHeight: '500px', width: '100%', overflow: 'auto'}}>{renderTable()}</Box>

        <FooterContainer>
          <ActionButton onClick={handleToggleReadStatus}>
            <Trans>
              {checkIsSuspended(type) ? 'Отметить как непрочитанное' : 'Отметить прочитанным'}
            </Trans>
          </ActionButton>
          <FooterText>
            <Trans>
              Прочитанные хранятся <br /> на <a href='/recommendations'>странице рекомендаций</a>
            </Trans>
          </FooterText>
        </FooterContainer>
      </ModalContent>
    </Modal>
  );
};

export default RSAModal;
